import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import Rating from "../common/Rating";
import "../../styles/projectCard.scss";

// * Svgs
import { ReactComponent as EyeWhiteSvg } from "../../assets/icons/eye-white.svg";
import { ReactComponent as LikeSvg } from "../../assets/icons/like.svg";
import { ReactComponent as LikedSvg } from "../../assets/icons/liked.svg";
import AppContext from "../../AppContext";
import { langComponent } from "../../constants";
import { useLocation } from "react-router-dom";
import useWorks from "../../hooks/useWorks";

import { ReactComponent as PanoramaIndicatorSvg } from "../../assets/icons/360-indicator.svg";
import { ReactComponent as ImageIndicatorSvg } from "../../assets/icons/image-indicator.svg";

import programSketchUpImage from "../../assets/icons/programs/sketchUp.png";
import program3dsMaxImage from "../../assets/icons/programs/3dsMax.png";
import programRevitImage from "../../assets/icons/programs/autodeskRevit.png";
import programCinema4DImage from "../../assets/icons/programs/cinema4d.png";
import programUnrealImage from "../../assets/icons/programs/unreal.png";
import programRhinoImage from "../../assets/icons/programs/rhino.png";
import programAutocadImage from "../../assets/icons/programs/autocad.png";
import programArchicadImage from "../../assets/icons/programs/archicad.png";

import programVRayImage from "../../assets/icons/programs/vray.png";
import programCoronaImage from "../../assets/icons/programs/corona.png";
import programD5RenderImage from "../../assets/icons/programs/d5render.png";
import programEnscapeImage from "../../assets/icons/programs/enscape.jpg";
import programLumionImage from "../../assets/icons/programs/lumion.png";

const assetsURI = import.meta.env.ASSETS_URI;

const PROGRAMS = {
  1: programSketchUpImage,
  2: program3dsMaxImage,
  3: programRevitImage,
  4: programCinema4DImage,
  5: programUnrealImage,
  6: programRhinoImage,
  7: programAutocadImage,
  8: programArchicadImage,
};

const RENDERERS = {
  1: programVRayImage,
  2: programCoronaImage,
  3: programLumionImage,
  4: programEnscapeImage,
  5: programD5RenderImage,
};

const ProjectCard = ({
  _id,
  banner,
  name,
  creator,
  designType: _designType,
  isInCollection: _isInCollection,
  // height,
  views,
  type,
  program,
  renderer,
  score,
}) => {
  const {
    user,
    viewProject,
    plans,
    viewProfile,
    getLang,
    addLikes,
    removeLikes,
  } = useContext(AppContext);
  const TranslationPlan = getLang(langComponent.plan);
  const [isLiked, setIsLiked] = useState(undefined);
  const [size, setSize] = useState("m");
  const [rating, setRating] = useState(0);
  const location = useLocation();
  const [height, setHeight] = useState(0);

  const ref = useCallback((node) => {
    if (node !== null) {
      const resizeObserver = new ResizeObserver((entries) => {
        const _width = entries[0].contentRect.width;

        // setHeight(_width);

        if (_width <= 270) {
          setSize("xs");
          return;
        }

        if (_width <= 350) {
          setSize("s");
          return;
        }

        setSize("m");
      });
      resizeObserver.observe(node);
    }
  }, []);

  useEffect(() => {
    setIsLiked(user?.likes?.includes(_id));
  }, [user?.likes]);

  const handleViewWorkClick = (_id) => {
    viewProject(_id);
  };

  const handleLikeClick = async (e) => {
    e.stopPropagation();

    await addLikes(_id);
    setIsLiked(true);
  };

  const handleUnlikeClick = async (e) => {
    e.stopPropagation();

    await removeLikes(_id);
    setIsLiked(false);
  };

  const handleViewProfileClick = () => {
    viewProfile(creator._id, location);
  };

  const getUserPlan = () => {
    // * Get user plan
    const usersPlan = plans.find((p) => p._id === creator.plan);

    if (usersPlan.name === "Guest") return null;

    return (
      <div
        className={`project-preview-footer-content-name__plan project-preview-footer-content-name__plan--${usersPlan.name.toLowerCase()}`}
      >
        {TranslationPlan(usersPlan.name)}
      </div>
    );
  };

  const getViewCount = () => {
    if (!views) return 0;
    if (views < 1000) return views;
    return `${(views / 1000).toFixed(1)}k`;
  };

  let styles = {};

  if (height) {
    styles.height = `${height}px`;
  }

  const _classNames = ["project-preview", `project-preview--${size}`];

  return (
    <div
      className={_classNames.join(" ")}
      style={styles}
      ref={ref}
      onClick={() => handleViewWorkClick(_id)}
      role="button"
    >
      <div className="project-preview-banner">
        <div className="project-preview-banner-image">
          <img alt="work" src={`${assetsURI}/${banner}`} />
        </div>
      </div>
      <div className="project-preview-header">
        {views && views > 0 ? (
          <div className="project-preview-footer-content-views">
            <EyeWhiteSvg />
            {getViewCount()}
          </div>
        ) : (
          <></>
        )}
        <div className="project-preview-footer-content-cta">
          {isLiked && (
            <LikedSvg
              className="project-preview-footer-content-cta__action"
              onClick={handleUnlikeClick}
            />
          )}
          {!isLiked && (
            <LikeSvg
              className="project-preview-footer-content-cta__action"
              onClick={handleLikeClick}
            />
          )}
        </div>
        <div
          className="project-preview__indicator"
          style={{
            marginTop: type === 1 ? -6 : 0,
            marginBottom: type === 1 ? -6 : 0,
          }}
        >
          {type === 1 && <PanoramaIndicatorSvg width={36} />}
          {type === 3 && <ImageIndicatorSvg width={24} />}
        </div>
      </div>
      {size === "xs" && <></>}
      <div className="project-preview-footer">
        <div className="project-preview-footer-content">
          <img
            className="project-preview-footer-content__avatar"
            alt="owner-avatar"
            src={`${assetsURI}/${creator.avatar}`}
            onClick={handleViewProfileClick}
            role="presentation"
          />
          <div className="project-preview-footer-content-info">
            <div className="project-preview__rating">
              {score > 0 && (
                <Rating score={score} readOnly size={18} color="yellow" />
              )}
            </div>
            <div
              className="project-preview-footer-content-name"
              onClick={handleViewProfileClick}
              role="button"
            >
              <div className="project-preview-footer-content-name__value">
                {name}
              </div>
            </div>
          </div>
          <div className="project-preview-footer-programs">
            {program && (
              <img
                className="project-preview-footer-programs__icon"
                src={PROGRAMS[program]}
                width={24}
              />
            )}
            {renderer && (
              <img
                className="project-preview-footer-programs__icon"
                src={RENDERERS[renderer]}
                width={24}
              />
            )}
          </div>
          {!["s", "xs"].includes(size) && getUserPlan()}
        </div>
      </div>
    </div>
  );
};

ProjectCard.propTypes = {};

export default ProjectCard;
